import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import PromptList from '../components/PromptManager/PromptList';
import PromptEditor from '../components/PromptManager/PromptEditor';
import VersionHistory from '../components/PromptManager/VersionHistory';
import PromptTestPanel from '../components/PromptManager/PromptTestPanel';
import OutputComparisonModal from '../components/PromptManager/OutputComparisonModal';
import API_ENDPOINTS from '../config/api';
import useAdminInstance, { ADMIN_INSTANCE_STORAGE_KEY } from '../hooks/useAdminInstance';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { useTranslation } from '../hooks/useTranslation';

const Container = styled.div`
  height: 100vh;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a2e 100%);
  display: flex;
  flex-direction: column;
`;

const Main = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
`;

const EditorColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 12px;
  color: #666;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${p => p.$color || '#22c55e'};
  display: inline-block;
`;

// Always visible: what saving here changes. Promptit edits global prompts only;
// instance overrides are listed here and edited in Valmistelu › Promptit (#689, #705).
const ScopeBar = styled.div`
  padding: 8px 24px;
  background: rgba(0, 153, 255, 0.06);
  border-bottom: 1px solid rgba(0, 153, 255, 0.15);
  font-size: 12px;
  color: #9ab;
  line-height: 1.5;

  strong {
    color: #e8f4ff;
  }
`;

const OverrideNote = styled.div`
  margin-top: 4px;
  color: #f59e0b;

  a { color: #fbbf24; }
`;

const readRememberedInstance = () => {
  try {
    return localStorage.getItem(ADMIN_INSTANCE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const PromptManagerScreen = () => {
  // Server page: never write an instance into the URL. The instance only
  // preselects test data (language, context) in the test panels.
  const { instanceId: urlInstanceId } = useAdminInstance({ restoreFromStorage: false });
  const testInstanceId = urlInstanceId || readRememberedInstance();

  const [prompts, setPrompts] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [template, setTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [error, setError] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pendingKey, setPendingKey] = useState(null); // prompt to open once unsaved edits are discarded
  const { t } = useTranslation();

  useUnsavedChangesGuard(dirty, t('common.unsavedLeave'));

  const fetchPrompts = useCallback(async () => {
    try {
      const url = API_ENDPOINTS.ADMIN_PROMPTS();
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPrompts(data);
    } catch (err) {
      console.error('[PromptManager] Failed to load prompts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

  const fetchTemplate = useCallback(async (key) => {
    if (!key) { setTemplate(null); setVersions([]); return; }
    try {
      const [templateRes, versionsRes] = await Promise.all([
        fetch(API_ENDPOINTS.ADMIN_PROMPT(key), { credentials: 'include' }),
        fetch(API_ENDPOINTS.ADMIN_PROMPT_VERSIONS(key), { credentials: 'include' }),
      ]);

      if (!templateRes.ok) throw new Error(`HTTP ${templateRes.status}`);
      const templateData = await templateRes.json();
      setTemplate(templateData);

      if (versionsRes.ok) {
        const versionsData = await versionsRes.json();
        setVersions(versionsData.versions || []);
      }
    } catch (err) {
      console.error('[PromptManager] Failed to load template:', err);
      setError(err.message);
    }
  }, []);

  useEffect(() => { fetchTemplate(selectedKey); }, [selectedKey, fetchTemplate]);

  const openPrompt = (key) => {
    setSelectedKey(key);
    setError(null);
    setDirty(false);
    setPendingKey(null);
  };

  const handleSelect = (key) => {
    if (key === selectedKey) return;
    if (dirty) setPendingKey(key);
    else openPrompt(key);
  };

  const handleSave = async (data) => {
    if (!selectedKey || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT(selectedKey), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Save failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      await fetchTemplate(selectedKey);
      await fetchPrompts();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (version) => {
    if (!selectedKey || reverting) return;
    setReverting(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_PROMPT_REVERT(selectedKey), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ version }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Revert failed' }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      await fetchTemplate(selectedKey);
      await fetchPrompts();
    } catch (err) {
      setError(err.message);
    } finally {
      setReverting(false);
    }
  };

  const overrides = prompts.find(p => p.key === selectedKey)?.overrides || [];

  return (
    <Container>
      <AdminHeader />

      <ScopeBar>
        <strong>Globaalit promptit.</strong> Tallennus muuttaa promptia kaikissa instansseissa, joilla ei ole omaa versiota.
        Testipaneelien instanssivalinta vaikuttaa vain testiajon kieleen ja kontekstiin.
        {template && overrides.length > 0 && (
          <OverrideNote>
            Oma versio käytössä:{' '}
            {overrides.map((o, i) => (
              <React.Fragment key={o.instanceId}>
                {i > 0 && ', '}
                <Link to={`/admin/instance-prompts?instance=${o.instanceId}&key=${encodeURIComponent(template.key)}`}>
                  {o.instanceTitle || o.instanceId}
                </Link>
                {` (v${o.version}, globaali v${template.version})`}
              </React.Fragment>
            ))}
            . Kuution omaa versiota muokataan sen kohdassa Valmistelu › Promptit.
          </OverrideNote>
        )}
      </ScopeBar>

      {error && (
        <StatusBar>
          <StatusDot $color="#ef4444" />
          <span style={{ color: '#f87171' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', marginLeft: 'auto', fontSize: 12 }}
          >
            Sulje
          </button>
        </StatusBar>
      )}

      <Main>
        <PromptList
          prompts={prompts}
          selectedKey={selectedKey}
          onSelect={handleSelect}
          search={search}
          onSearchChange={setSearch}
          category={category}
          onCategoryChange={setCategory}
        />

        <EditorColumn>
          <PromptEditor
            template={template}
            onSave={handleSave}
            saving={saving}
            onDirtyChange={setDirty}
          />
          {template && (
            <PromptTestPanel
              template={template}
              instanceId={testInstanceId}
            />
          )}
        </EditorColumn>

        {template && (
          <VersionHistory
            versions={versions}
            currentTemplate={template}
            onRevert={handleRevert}
            reverting={reverting}
            onOpenComparison={() => setShowComparison(true)}
            promptKey={selectedKey}
            onRefreshVersions={() => fetchTemplate(selectedKey)}
          />
        )}
      </Main>

      {showComparison && template && (
        <OutputComparisonModal
          promptKey={template.key}
          promptName={template.name || template.key}
          versions={versions}
          currentTemplate={template}
          instanceId={testInstanceId}
          onClose={() => setShowComparison(false)}
          onRefreshVersions={() => fetchTemplate(template.key)}
        />
      )}

      {pendingKey && (
        <ConfirmDialog
          title={t('common.unsavedTitle')}
          message={t('common.unsavedMessage')}
          confirmLabel={t('common.discardChanges')}
          destructive
          onConfirm={() => openPrompt(pendingKey)}
          onCancel={() => setPendingKey(null)}
        />
      )}
    </Container>
  );
};

export default PromptManagerScreen;
